import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { MdArrowOutward, MdArrowForward } from 'react-icons/md'
import { projects, hasCaseStudy } from './projectData'

// A project with a written study opens the study; the rest still go straight
// out to the live site.
const Row = ({ project, children, ...rest }) =>
  hasCaseStudy(project) ? (
    <Link to={`/projects/${project.slug}`} {...rest}>
      {children}
    </Link>
  ) : (
    <a href={project.liveURL} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  )

// Option A — editorial row list with a single preview image that
// chases the cursor (eased) as you move down the list. Desktop only;
// on touch / small screens it falls back to a plain list.
export const WorkCursorPreview = () => {
  const [active, setActive] = useState(null)
  const previewRef = useRef(null)
  const target = useRef({ x: 0, y: 0 })
  const current = useRef({ x: 0, y: 0 })
  const raf = useRef(0)

  useEffect(() => {
    const tick = () => {
      current.current.x += (target.current.x - current.current.x) * 0.12
      current.current.y += (target.current.y - current.current.y) * 0.12
      if (previewRef.current) {
        previewRef.current.style.transform = `translate3d(${current.current.x}px, ${current.current.y}px, 0)`
      }
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const onMove = (e) => {
    target.current = { x: e.clientX + 24, y: e.clientY - 120 }
  }

  return (
    <div onMouseMove={onMove} className=" relative">
      <ul className=" border-t border-ink/15">
        {projects.map((p, i) => (
          <li key={p.title}>
            <Row
              project={p}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              className=" group flex items-baseline justify-between gap-4 border-b border-ink/15 py-5 md:py-7"
            >
              <span className=" flex items-baseline gap-4 md:gap-8 min-w-0">
                <span className=" font-mono text-xs text-ink/40 w-7 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className=" font-display text-2xl md:text-5xl text-maroon tracking-tight truncate transition-transform duration-300 group-hover:md:translate-x-2">
                  {p.title}
                </span>
              </span>
              <span className=" flex items-center gap-4 md:gap-8 shrink-0">
                <span className=" hidden md:block font-mono text-xs uppercase tracking-[0.15em] text-ink/45">
                  {p.stack.join(' / ')}
                </span>
                <span className=" hidden lg:block font-mono text-xs text-ink/35">{p.year}</span>
                {hasCaseStudy(p) ? (
                  <MdArrowForward className=" text-xl md:text-2xl text-ink/35 transition-all duration-300 group-hover:text-mint-deep group-hover:translate-x-1" />
                ) : (
                  <MdArrowOutward className=" text-xl md:text-2xl text-ink/35 transition-all duration-300 group-hover:text-mint-deep group-hover:rotate-45" />
                )}
              </span>
            </Row>
          </li>
        ))}
      </ul>

      {/* cursor-tracking preview (desktop pointer only) */}
      <div
        ref={previewRef}
        className=" pointer-events-none fixed left-0 top-0 z-40 hidden w-64 overflow-hidden rounded-xl shadow-2xl [@media(pointer:fine)]:block"
        style={{ opacity: active === null ? 0 : 1, transition: 'opacity 0.25s ease' }}
      >
        <div className=" aspect-[3/2] w-full bg-maroon/10">
          {active !== null && (
            <img src={projects[active].path} alt="" className=" h-full w-full object-cover" />
          )}
        </div>
      </div>
    </div>
  )
}
